// React
import PropTypes from "prop-types";
import { useContext, useEffect, useMemo, useState } from "react";

// @mui material components
import {
  Autocomplete,
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";

import { gql, useMutation, useQuery } from "@apollo/client";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import SoftInputNumber from "components/SoftInputNumberUpgrade";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "80%", sm: "50%", md: "40%", xl: "30%" },
  overflowY: "auto",
  p: 3,
};

const productoInicial = {
  id: "",
  nombre: "",
  limiteReservas: 0,
  idTurnoSede: -1,
  tipoMenu: -1,
};

const toTimeLabel = (value = "") => String(value || "").slice(0, 5);

const normalizeTime = (value = "") => {
  const time = String(value || "").trim();
  if (!time) return "12:00:00";
  if (/^\d{2}:\d{2}$/.test(time)) return `${time}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time;
  return "12:00:00";
};

export default function ModalAddProducto({ open, handleClose, refetch, fecha }) {
  const { handleSnackbar } = useContext(MessageManager);
  const { user } = useContext(UserContext);
  const [producto, setProducto] = useState(productoInicial);

  const idSedeActual = Number(user?.sede?.id || user?.idSede || localStorage.getItem("idSede") || 1);

  const [saveMenuDelDia, { loading }] = useMutation(
    gql`
      mutation saveMenuDelDia($input: MenuDelDiaInput!) {
        saveMenuDelDia(input: $input) {
          id
        }
      }
    `
  );

  const { data: productos } = useQuery(
    gql`
      query productos($filter: JSON) {
        productos(filter: $filter) {
          id
          nombre
          precioCarta
          categoriaProducto {
            id
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      variables: {
        filter: {
          idCategoria: [16, 19],
        },
      },
    }
  );

  const { data: turnosData, loading: loadingTurnos } = useQuery(
    gql`
      query turnosSedeMenu($idSede: ID!) {
        turnosSede(idSede: $idSede) {
          id
          nombre
          horaInicio
          horaFin
          activo
        }
      }
    `,
    {
      variables: {
        idSede: idSedeActual,
      },
      fetchPolicy: "no-cache",
      skip: !idSedeActual,
    }
  );

  const turnosDisponibles = useMemo(
    () => (turnosData?.turnosSede || []).filter((turno) => turno?.activo !== false),
    [turnosData]
  );

  const handleSave = () => {
    const turnoSeleccionado = turnosDisponibles.find(
      (turno) => String(turno.id) === String(producto?.idTurnoSede)
    );

    if (!turnoSeleccionado) {
      handleSnackbar("Debe seleccionar un turno valido", "warning");
      return;
    }

    const [horas, minutos, segundos] = normalizeTime(turnoSeleccionado?.horaFin)
      .split(":")
      .map((value) => Number(value || 0));

    saveMenuDelDia({
      variables: {
        input: {
          idProducto: producto?.id,
          idTurnoSede: turnoSeleccionado?.id,
          nombre: producto.nombre,
          fechaMenu: dayjs(fecha)
            .set("hour", horas)
            .set("minute", minutos)
            .set("second", segundos)
            .format("YYYY-MM-DD HH:mm:ss"),
          limiteReservas: Number(producto.limiteReservas || 0),
          limiteReservasSedeSP: Number(producto.limiteReservas || 0),
          limiteReservasSedeTR: Number(producto.limiteReservas || 0),
          limiteReservasSedeC: Number(producto.limiteReservas || 0),
          turno: turnoSeleccionado?.nombre,
        },
      },
    })
      .then(() => {
        handleSnackbar("Guardado correctamente", "success");
        refetch();
        handleClose();
      })
      .catch((error) => {
        const errorMessage = error?.graphQLErrors?.[0]?.message || "Error al guardar";
        handleSnackbar(errorMessage, "error");
      });
  };

  useEffect(() => {
    if (!open) {
      setProducto(productoInicial);
    }
  }, [open]);

  const handleChangeProducto = (e) => {
    setProducto({
      ...producto,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} display="flex" flexDirection="row" justifyContent="space-between">
              <Grid></Grid>
              <SoftTypography variant="h6" align="center">
                {`Agregar Menu al dia ${fecha ? dayjs(fecha).format("DD/MM/YYYY") : ""}`}
              </SoftTypography>
              <Grid>
                <Tooltip title="Cerrar" placement="top">
                  <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                    close
                  </Icon>
                </Tooltip>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor="tipoMenu">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Que desea cargar?
                </SoftTypography>
              </InputLabel>
              <Select
                value={producto?.tipoMenu}
                name="tipoMenu"
                onChange={handleChangeProducto}
                fullWidth
              >
                <MenuItem value={-1} disabled>
                  Elija si quiere cargar un menu o una entrada
                </MenuItem>
                <MenuItem value={16}>Menu</MenuItem>
                <MenuItem value={19}>Entrada</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor="precioCarta">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Buscar producto
                </SoftTypography>
              </InputLabel>
              <Autocomplete
                disabled={producto?.tipoMenu == null}
                value={producto}
                options={
                  productos?.productos
                    ?.filter((p) => p?.categoriaProducto?.id == producto?.tipoMenu)
                    ?.map((option) => option) || []
                }
                getOptionLabel={(option) => option.nombre}
                noOptionsText="No hay resultados"
                onChange={(e, newValue) => {
                  if (newValue) {
                    setProducto((prev) => ({
                      ...prev,
                      ...newValue,
                      precio: newValue.precioCarta,
                    }));
                  } else {
                    setProducto(productoInicial);
                  }
                }}
                renderInput={(params) => <TextField {...params} placeholder="Buscar" />}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor="idTurnoSede">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Turno
                </SoftTypography>
              </InputLabel>
              <Select
                value={producto?.idTurnoSede}
                name="idTurnoSede"
                onChange={handleChangeProducto}
                fullWidth
                sx={{
                  fontStyle: producto?.idTurnoSede === -1 ? "italic" : "normal",
                }}
              >
                <MenuItem
                  value={-1}
                  disabled
                  sx={{
                    display: "none",
                  }}
                >
                  Elija un turno
                </MenuItem>
                {turnosDisponibles.map((turno) => (
                  <MenuItem key={turno.id} value={turno.id}>
                    {`${turno.nombre} ${toTimeLabel(turno.horaInicio)} - ${toTimeLabel(turno.horaFin)}`}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor="limiteReservas">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Limite de reservas
                </SoftTypography>
              </InputLabel>
              <SoftInputNumber
                name="limiteReservas"
                value={producto?.limiteReservas}
                onChange={handleChangeProducto}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftButton
                color="uproGreen"
                circular
                fullWidth={{
                  xs: true,
                  sm: false,
                }}
                onClick={handleSave}
                disabled={
                  loading ||
                  loadingTurnos ||
                  !producto.nombre ||
                  producto.idTurnoSede === -1
                }
              >
                {false ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Guardar
              </SoftButton>
            </Grid>
          </Grid>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAddProducto.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  fecha: PropTypes.string,
};
